import { DashboardOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router'
import { useParams } from 'react-router-dom'
import AuthBaseLayout from '../AuthBaseLayout'
import SubMenu from 'Components/Menu/SubMenu'
import MenuItem from 'Components/Menu/MenuItem'
import { HomeRow, MenuTitle } from 'Layouts/styled'
import OrganisationAvatar from 'Components/Avatar/OrganisationAvatar'
import navMenuStyles from 'Styles/NavMenu.module.scss'
import { useCurrentOrganisation } from 'Hooks/useCurrentOrganisation'
import { AuthorizationWrapperV2 } from 'Models/AuthorizeV2/checkAuthorizationV2'
import PolicyMappings from 'Utils/policyMapping'
import UnauthorizedScreen from 'Components/Auth/UnauthorizedScreen'

const buildMenus = ['/configuration', '/integration', '/sms', '/prompts']
const monitorMenus = ['/interactions', '/analytics']

export const SmsAgentConfigLayout = () => {
  const dispatch = useDispatch()
  const flowLayout: any = useSelector<any>((s) => s.flowLayout)

  const params = useParams()
  const location = useLocation()
  const [activeSubMenu, setActiveSubMenu] = useState<string>('')
  const { organisation } = useCurrentOrganisation()
  const flowUuid = params?.flow_uuid || ''
  const orgUuid = params?.organisation_uuid || ''

  useEffect(() => {
    dispatch({
      type: 'organisations/retrieve',
      uuid: params?.organisation_uuid,
    })
  }, [params?.organisation_uuid, dispatch])

  useEffect(() => {
    dispatch({
      type: 'flowLayout/setSmsFlow',
      uuid: params.flow_uuid,
      organisationUuid: params.organisation_uuid,
    })
  }, [dispatch, flowUuid, orgUuid, params.flow_uuid, params.organisation_uuid])

  const selected: any = useMemo(() => {
    if (buildMenus.some((x) => location.pathname.includes(x))) {
      return { build: true }
    } else if (monitorMenus.some((x) => location.pathname.includes(x))) {
      return { monitor: true }
    }
    return false
  }, [location.pathname])

  return (
    <AuthorizationWrapperV2
      requiredPolicies={PolicyMappings.smsAgentsView}
      unauthorizedComponent={<UnauthorizedScreen />}
    >
      <AuthBaseLayout
        collapsibleSideNav={true}
        sideNavTheme="dark"
        className={[navMenuStyles['menu-item'], navMenuStyles['menu-item__dark']].join(' ')}
        sideMenuProps={{
          style: {
            backgroundColor: 'var(--blue-600)',
          },
          theme: 'dark',
          defaultOpenKeys: [],
        }}
        menu={[
          <SubMenu
            key="config-sub-menu"
            title={'Build'}
            submenuKey={'build'}
            activeKey={activeSubMenu}
            icon={<DashboardOutlined />}
            defaultOpen={selected.build}
            selected={selected.build}
            theme="dark"
            onOpen={() => setActiveSubMenu('build')}
          >
            <MenuItem
              key="configure"
              subMenuItem
              to={`configuration`}
              selected={location.pathname.includes('configuration')}
              theme="dark"
            >
              Configure
            </MenuItem>
            <MenuItem
              key="integrations"
              subMenuItem
              to={`integration`}
              selected={location.pathname.includes('integration')}
              theme="dark"
            >
              Integrations
            </MenuItem>
            <MenuItem
              key="prompts"
              subMenuItem
              to={`prompts`}
              selected={location.pathname.includes('prompts')}
              theme="dark"
            >
              Prompts
            </MenuItem>
          </SubMenu>,
          <AuthorizationWrapperV2
            requiredPolicies={[
              ...PolicyMappings.appAnalyticsViewTab,
              ...PolicyMappings.appCallsViewCalls,
            ]}
          >
            <SubMenu
              key="monitor-sub-menu"
              title="Monitor"
              activeKey={activeSubMenu}
              submenuKey={'monitor'}
              icon={<DashboardOutlined />}
              theme="dark"
              selected={selected.monitor}
              defaultOpen={selected.monitor}
              onOpen={() => setActiveSubMenu('monitor')}
            >
              <MenuItem
                key="interactions"
                to="interactions"
                theme="dark"
                selected={location.pathname.includes('interactions')}
                subMenuItem
              >
                SMS
              </MenuItem>
              <MenuItem
                disabled
                key="analytics"
                to="analytics"
                theme="dark"
                selected={location.pathname.includes('analytics')}
                subMenuItem
              >
                Analytics
              </MenuItem>
            </SubMenu>
          </AuthorizationWrapperV2>,
        ]}
        headerRender={({ theme, collapsed }: any) => {
          return (
            <HomeRow theme={theme}>
              <OrganisationAvatar name={organisation?.name} />
              {!collapsed ? (
                <MenuTitle theme={theme}>{flowLayout?.flow?.name || ''}</MenuTitle>
              ) : null}
            </HomeRow>
          )
        }}
        id="sms-config-layout"
      >
        <Outlet />
      </AuthBaseLayout>
    </AuthorizationWrapperV2>
  )
}
